import { Button } from "@mui/material";
import { some, times } from "lodash-es";
import { useEffect, useEffectEvent, useState, type ReactNode } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useTranslations } from "use-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { ErrorPanel } from "@/components/Errors/ErrorPanel";
import { NoFilteringResults } from "@/components/Errors/NoFilteringResults";
import { IconAirplaneCrossed } from "@/components/Icons";
import { SearchResultsWhatsAppBanner } from "@/components/WhatsApp/WhatsAppBanners";
import { Header } from "@/features/flights/components/flight-list/Header";
import { FlightsListDrawerComponent } from "@/features/flights/components/flight-list/types";
import { FlightSearchForm } from "@/features/flights/components/FlightSearchForm/FlightSearchForm";
import { SingleAvailableFlightOfferSkeleton } from "@/features/flights/components/SingleAvailableFlightOfferSkeleton";
import { useFlightFiltersContext } from "@/features/flights/contexts/FlightFiltersContext";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import { useFlightSortContext } from "@/features/flights/contexts/FlightSortContext";
import type { FlightSearchQuery } from "@/features/flights/hooks/flightSearch/types";
import { useFlightSearchForm } from "@/features/flights/hooks/flightSearch/useFlightSearchForm";
import type {
  FlightSearchType,
  FlightType,
  MappedFlightOffer,
} from "@/features/flights/types/types";
import type { FlightSearchFormValues } from "@/features/flights/utils/flightsValidation";
import { TripBanner } from "@/features/trips/components/TripBanner";
import {
  FlightTrackingEvents,
  rudderAnalytics,
  TripTrackingEvents,
} from "@/lib/rudderstack";
import { getDateString } from "@/utils/dates";

import { SingleAvailableFlightV2 } from "../SingleAvailableFlightV2";

import { AirlinesFilterDrawer } from "./AirlinesFilterDrawer";
import { FlightsFilters } from "./flight-filters/FlightsFilters";
import { FlightsSortDrawer } from "./FlightsSortDrawer";
import { StopsFilterDrawer } from "./StopsFilterDrawer";

type Props = {
  showHeader?: boolean;
  title?: ReactNode;
  searchQuery: { query: FlightSearchQuery; type: FlightSearchType | null };
  direction: "departure" | "return";
  onFlightSelect: (
    offer: MappedFlightOffer,
    flight: FlightType,
    queryType: FlightSearchType | null,
  ) => void;
  isLoadingAdditionalData?: boolean;
  onAfterSearchSubmit?: () => void;
};

export const FlightsList = ({
  showHeader = true,
  title,
  searchQuery,
  direction,
  onFlightSelect,
  isLoadingAdditionalData = false,
  onAfterSearchSubmit,
}: Props) => {
  const { sortValue, updateSortValue } = useFlightSortContext();
  const { defaultFilters, filters, activeFilters, updateFilters } =
    useFlightFiltersContext();
  const areFiltersDirty = some(activeFilters, Boolean);

  const [drawerComponent, setDrawerComponent] =
    useState<FlightsListDrawerComponent | null>(null);
  const t = useTranslations();

  const { searchData } = useFlightSearchDataContext();
  const { handleSubmit } = useFlightSearchForm();

  const {
    isError,
    hasReachedEnd,
    fetchNextPage,
    result,
    isLoading: isSearchQueryLoading,
    isFetching,
    isEmpty,
  } = searchQuery.query;

  const sendNoFlightResultsFoundEvent = useEffectEvent(
    (source: FlightSearchType) => {
      if (areFiltersDirty || !searchData?.from || !searchData?.to) return;

      const travellersCount =
        searchData.adult + searchData.child + searchData.infantWithoutSeat;

      rudderAnalytics.track(FlightTrackingEvents.NO_FLIGHT_RESULTS_FOUND, {
        originId: searchData.from.id,
        originName: searchData.from.name,
        destinationId: searchData.to.id,
        destinationName: searchData.to.name,
        dateFrom: getDateString(searchData.dates.departure),
        dateTo: searchData.dates.return
          ? getDateString(searchData.dates.return)
          : undefined,
        travellers: travellersCount,
        cabinClass: searchData.cabinClass,
        source,
      });
    },
  );

  const handleSearchSubmit = (values: FlightSearchFormValues) => {
    handleSubmit(values);
    setDrawerComponent(null);
    if (onAfterSearchSubmit) onAfterSearchSubmit();
  };

  useEffect(() => {
    if (searchQuery.query.isSuccess && isEmpty && searchQuery.type) {
      sendNoFlightResultsFoundEvent(searchQuery.type);
    }
  }, [isEmpty, searchQuery.query.isSuccess, searchQuery.type]);

  const isLoading = (!result || isEmpty) && isSearchQueryLoading;

  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: !hasReachedEnd,
    onLoadMore: () => {
      rudderAnalytics.track(FlightTrackingEvents.USER_SEARCHED_FOR_MORE_OFFERS);
      fetchNextPage();
    },
    disabled: isError || isLoadingAdditionalData,
    rootMargin: "0px 0px 40px 0px",
  });

  return (
    <div className="flex grow flex-col px-4">
      {showHeader ? (
        <Header
          onFilterButtonClicked={setDrawerComponent}
          selectedSortValue={sortValue}
          isLoading={isSearchQueryLoading || isLoadingAdditionalData}
          activeFilters={activeFilters}
          onSearchDrawerOpen={() =>
            setDrawerComponent(FlightsListDrawerComponent.SEARCH)
          }
        />
      ) : null}

      {!isLoading && isEmpty ? (
        <div className="mt-12 flex grow flex-col items-center justify-between gap-8">
          {areFiltersDirty ? (
            <NoFilteringResults
              scope="flights"
              title={t("flights.noFlightsMatchFilters")}
              onClearFilterClick={() => updateFilters(defaultFilters)}
            />
          ) : (
            <ErrorPanel
              icon={IconAirplaneCrossed}
              primaryText={t("flights.emptyState")}
              secondaryText={t("flights.emptyStateDesc")}
            >
              <Button
                onClick={(e) => {
                  setDrawerComponent(FlightsListDrawerComponent.SEARCH);
                  e.currentTarget.blur();
                }}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ borderRadius: "var(--radius-2xl)" }}
              >
                {t("editSearch")}
              </Button>
            </ErrorPanel>
          )}

          <SearchResultsWhatsAppBanner />
        </div>
      ) : (
        <div className="mt-4 w-full">
          <ul className="space-y-2" aria-label={t("flights.availableFlights")}>
            {isLoading ? (
              times(3, (index) => (
                <li key={index}>
                  <SingleAvailableFlightOfferSkeleton />
                </li>
              ))
            ) : (
              <>
                {title}
                {result?.map((offer, index) => {
                  if (offer.type === "PACKAGE_BANNER") {
                    const eventData = {
                      package_id: offer.packageId,
                      package_name: offer.packageName,
                      banner_name: offer.bannerTitle,
                      banner_url: offer.imageUrl,
                      list: "flight",
                      city_name: offer.cityName,
                      city_id: offer.cityId,
                      position: offer.position,
                    } as const;

                    return (
                      <li key={`banner-${offer.packageId}-${index}`}>
                        <TripBanner
                          tripId={offer.packageId}
                          src={offer.imageUrl}
                          title={offer.bannerTitle}
                          onViewed={() => {
                            rudderAnalytics.track(
                              TripTrackingEvents.TRIP_PROMO_BANNER_VIEWED,
                              eventData,
                            );
                          }}
                          onClicked={() => {
                            rudderAnalytics.track(
                              TripTrackingEvents.TRIP_PROMO_BANNER_CLICKED,
                              eventData,
                            );
                          }}
                        />
                      </li>
                    );
                  }

                  return offer.flightsOverview.map((fo) => {
                    const flight =
                      direction === "departure"
                        ? fo.departureFlight
                        : fo.returnFlight;

                    if (!flight) return null;

                    return (
                      <li
                        key={`${offer.index}-${flight.key}-${fo.itineraryIndex}`}
                      >
                        <SingleAvailableFlightV2
                          flight={flight}
                          direction={direction}
                          offer={offer}
                          onClick={() =>
                            onFlightSelect(offer, flight, searchQuery.type)
                          }
                        />
                      </li>
                    );
                  });
                })}
                {isFetching || !hasReachedEnd || isLoadingAdditionalData
                  ? times(3, (index) => (
                      <li
                        key={index}
                        ref={index === 0 ? sentryRef : undefined}
                        aria-hidden={!isFetching}
                      >
                        <SingleAvailableFlightOfferSkeleton />
                      </li>
                    ))
                  : null}
              </>
            )}
          </ul>
        </div>
      )}

      <StopsFilterDrawer
        open={drawerComponent === FlightsListDrawerComponent.STOPS}
        defaultValue={defaultFilters?.stops}
        value={filters?.stops}
        onSubmit={(newStopsValue) => {
          if (!newStopsValue) return;
          updateFilters((prevFilters) =>
            prevFilters ? { ...prevFilters, stops: newStopsValue } : null,
          );
          setDrawerComponent(null);
        }}
        onClose={() => setDrawerComponent(null)}
      />

      <AirlinesFilterDrawer
        open={drawerComponent === FlightsListDrawerComponent.AIRLINES}
        value={filters?.selectedAirlines}
        defaultFilter={defaultFilters?.selectedAirlines}
        allAirlines={filters?.airlines}
        onSubmit={(newAirlinesValue) => {
          updateFilters((prevFilters) =>
            prevFilters
              ? { ...prevFilters, selectedAirlines: newAirlinesValue }
              : null,
          );
          setDrawerComponent(null);
        }}
        onCloseClick={() => setDrawerComponent(null)}
      />

      <FlightsSortDrawer
        open={drawerComponent === FlightsListDrawerComponent.SORT}
        value={sortValue}
        onSubmit={(newSort) => {
          updateSortValue(newSort);
          setDrawerComponent(null);
        }}
        onClose={() => {
          setDrawerComponent(null);
        }}
      />

      <SwipeableDrawer
        open={drawerComponent === FlightsListDrawerComponent.ALL_FILTERS}
        onClose={() => setDrawerComponent(null)}
        hasFullHeight
      >
        {/* need to reset the state when drawer is open/closed */}
        {drawerComponent === FlightsListDrawerComponent.ALL_FILTERS &&
        filters &&
        defaultFilters ? (
          <FlightsFilters
            defaultFilters={defaultFilters}
            filters={filters}
            sortValue={sortValue}
            onFiltersChange={(newFilters, newSort) => {
              updateFilters((prevFilters) =>
                prevFilters
                  ? {
                      ...prevFilters,
                      ...newFilters,
                    }
                  : null,
              );
              updateSortValue(newSort);
              setDrawerComponent(null);
            }}
            onClose={() => setDrawerComponent(null)}
          />
        ) : null}
      </SwipeableDrawer>

      <SwipeableDrawer
        open={drawerComponent === FlightsListDrawerComponent.SEARCH}
        onClose={() => setDrawerComponent(null)}
      >
        <DrawerTopBar
          onCloseClick={() => setDrawerComponent(null)}
          title={t("search")}
        />

        <FlightSearchForm
          submitButtonContent={t("confirm")}
          showErrorsInline
          initialValues={searchData}
          onSubmit={handleSearchSubmit}
          analyticsSource="sticky"
        />
      </SwipeableDrawer>
    </div>
  );
};
